/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import Details from "@/components/Order/Details";
import IAppeal from "@/interfaces/appeal";
import Store from "@/store";
import AppealStore from "@/store/appeal";
import Chat from "@/components/Chat";
import { inject, observer } from "mobx-react";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  Grid,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  Container,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { OrderStore } from "@/store/orders";
import { IAsset, IOrder, IVWPost } from "@/interfaces";
import { UserStore } from "@/store/user";
import { TradingStore } from "@/store/Trading";
import { autorun } from "mobx";
import IDialog from "@/@types/Dialog";
import { ChatAppealStore } from "@/store/chat_appeals";
import IChatAppeal from "@/interfaces/chatAppeal";
import { ethers } from "ethers";
import * as BN from "bignumber.js";
import CommonStore, { UI_ACTION_STATE } from "@/store/common";
import { formatedBigNumber } from "@/utility";

const AppealPage = inject(
  Store.type.userStore,
  Store.type.orderStore,
  Store.type.tradingStore,
  Store.type.appealStore,
  Store.type.chatAppealStore,
  Store.type.commonStore,
)(
  observer((props: any) => {
    const userStore = props[Store.type.userStore] as UserStore;
    const orderStore = props[Store.type.orderStore] as OrderStore;
    const tradingStore = props[Store.type.tradingStore] as TradingStore;
    const appealStore = props[Store.type.appealStore] as AppealStore;
    const commonStore = props[Store.type.commonStore] as CommonStore;
    const { appeal_id } = useParams();
    const [appeal, setAppeal] = useState<IAppeal | null>(null);
    const chatAppealStore = props[
      Store.type.chatAppealStore
    ] as ChatAppealStore;

    const [confirmDialog, setConfirmDialog] = useState<IDialog | null>(null);

    const theme = useTheme();
    const navigate = useNavigate();
    const [side, setSide] = useState<"BUYER" | "SELLER" | null>(null);
    const [otherId, setOtherId] = useState("");
    const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
    const [selectedPost, setSelectedPost] = useState<IVWPost | null>(null);
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [asset, setAsset] = useState<IAsset | undefined>(undefined);
    const [chatAppealMessages, setChatAppealMessages] = useState<
      IChatAppeal[] | null
    >(null);

    useEffect(() => {
      let unsubscribeSelectedOrder: (() => void) | null = null;
      const disposeAutorun = autorun(async () => {
        if (appeal && userStore.userId) {
          if (appeal.created_by !== userStore.userId) {
            navigate("/");
            enqueueSnackbar("You do not have permission to view this appeal.", {
              variant: "error",
            });
          }
          if (appeal.order_id) {
            unsubscribeSelectedOrder = await orderStore.subscribeToOrder(
              appeal.order_id,
            );
          }
          setOtherId(appeal.admin_id);
        }
      });

      return () => {
        disposeAutorun();
        if (unsubscribeSelectedOrder) {
          unsubscribeSelectedOrder();
          unsubscribeSelectedOrder = null;
        }
      };
    }, [appeal, userStore]);

    useEffect(() => {
      let unsubscribeToAppealMessages: (() => void) | null = null;
      let unsubscribeToAppeal: (() => void) | null = null;
      if (appeal_id) {
        (async () => {
          unsubscribeToAppealMessages =
            await chatAppealStore.subscribeToAppealMessages(appeal_id);
          unsubscribeToAppeal = await appealStore.subscribeToAppealById(
            appeal_id,
          );
        })();
      }
      return () => {
        if (unsubscribeToAppealMessages) {
          unsubscribeToAppealMessages();
          unsubscribeToAppealMessages = null;
        }
        if (unsubscribeToAppeal) {
          unsubscribeToAppeal();
          unsubscribeToAppeal = null;
        }
      };
    }, [appeal_id]);

    useEffect(() => {
      const disposeAutorun = autorun(() => {
        if (chatAppealStore.appealMessages) {
          setChatAppealMessages(chatAppealStore.appealMessages);
        }
      });
      return () => {
        disposeAutorun();
      };
    }, [chatAppealStore]);

    useEffect(() => {
      const disposeAutorun = autorun(() => {
        setAppeal(appealStore.selectedAppeal);
      });
      return () => {
        disposeAutorun();
      };
    }, [appealStore]);

    useEffect(() => {
      let unsubscribeSelectedPost: (() => void) | null = null;
      const disposeAutorun = autorun(async () => {
        const order = orderStore.selectedOrder;
        if (order) {
          setSelectedOrder(orderStore.selectedOrder);
          if (!unsubscribeSelectedPost) {
            unsubscribeSelectedPost = await tradingStore.subscribeToPost(
              order.post_id,
            );
          }
          setSide(order.buyer_id === userStore.userId ? "BUYER" : "SELLER");
        }
      });
      return () => {
        disposeAutorun();
        if (unsubscribeSelectedPost) {
          unsubscribeSelectedPost();
          unsubscribeSelectedPost = null;
        }
      };
    }, [orderStore]);
    useEffect(() => {
      const disposeAutorun = autorun(() => {
        if (tradingStore.selectedPost) {
          setSelectedPost(tradingStore.selectedPost);
        }
      });
      return () => {
        disposeAutorun();
      };
    }, [tradingStore]);
    useEffect(() => {
      if (commonStore.assets && selectedPost) {
        setAsset(
          commonStore.assets.find(
            (asset) => asset.asset_id === selectedPost?.asset_id,
          ),
        );
      }
    }, [commonStore.assets, selectedPost]);

    const [appeal_details, setAppeal_details] = useState<{
      [key: string]: { value: string | undefined };
    } | null>(null);
    const [order_details, setOrder_details] = useState<{
      [key: string]: { value: string | undefined };
    } | null>(null);
    const [payment_details, setPayment_details] = useState<{
      [key: string]: { value: string | undefined; isImage?: boolean };
    } | null>(null);
    useEffect(() => {
      if (selectedOrder && appeal && userStore.profile?.username) {
        let appealDetails: {
          [key: string]: { value: string | undefined };
        } = {};

        appealDetails = {
          "Order Number": { value: selectedOrder.order_number || "" },
          Appellant: { value: userStore.profile.username },
          "Appeal Reason": { value: appeal.appeal_reason || "" },
        };

        if (selectedPost && appeal.created_by !== selectedPost.created_by) {
          appealDetails["Appealed Store"] = { value: selectedPost.title };
        }

        appealDetails["Appellee"] = { value: appeal.userByAppealTo?.username };
        appealDetails["Appellee's name"] = {
          value: `${appeal.userByAppealTo?.firstname} ${appeal.userByAppealTo?.firstname}`,
        };
        appealDetails["Appellee's Phone number"] = {
          value: appeal.userByAppealTo?.phone_number,
        };
        setAppeal_details(appealDetails);

        setOrder_details({
          "Order Number": { value: selectedOrder.order_number || "" },
          Token: { value: selectedOrder.asset.name },
          Quantity: {
            value: `${formatedBigNumber(selectedOrder.order_amount)} ${
              selectedOrder.asset.symbol
            }`,
          },
          Price: {
            value: `${formatedBigNumber(selectedOrder.price)} ${
              selectedOrder.currency
            }`,
          },
          Currency: { value: selectedOrder.currency },
          Payment: { value: selectedOrder.payment.payment_method.payment_name },
          "Used address": {
            value:
              side === "BUYER"
                ? selectedOrder.buyer_address
                : selectedOrder.seller_address,
          },
        });

        let paymentDetails: {
          [key: string]: { value: string | undefined; isImage?: boolean };
        } = {};
        if (side === "BUYER") {
          paymentDetails = selectedOrder.payment.payment_attributes.reduce(
            (
              acc: {
                [key: string]: any;
              },
              attr,
            ) => {
              const attrName = attr.payment_method_attribute.attribute_label;

              if (attrName) {
                if (attr.payment_method_attribute.is_image) {
                  acc[attrName] = {
                    value: attr.attribute_value,
                    isImage: true,
                  };
                } else {
                  acc[attrName] = { value: attr.attribute_value };
                }
              }

              return acc;
            },
            {},
          );
          paymentDetails["Seller address"] = {
            value: selectedOrder.seller_address,
          };
        } else {
          paymentDetails["Buyer Address"] = { value: selectedOrder.address };
        }
        if (userStore.userId === selectedOrder.buyer_id) {
          const orderAmount = ethers.utils.parseEther(
            BN.BigNumber(selectedOrder.order_amount)
              .multipliedBy(BN.BigNumber(selectedOrder.price))
              .dividedBy(BN.BigNumber(10).pow(18))
              .dividedBy(BN.BigNumber(10).pow(18))
              .decimalPlaces(2)
              .toString(),
          );
          paymentDetails["Transfer Amount"] = {
            value: `${formatedBigNumber(orderAmount)} ${
              selectedOrder.currency
            }`,
          };
          paymentDetails["Currency"] = { value: selectedOrder.currency };
        }
        if (userStore.userId === selectedOrder.seller_id) {
          paymentDetails["Transfer Amount"] = {
            value: `${formatedBigNumber(selectedOrder.order_amount)} ${
              selectedOrder.asset.symbol
            }`,
          };
          paymentDetails["Currency"] = { value: selectedOrder.asset.name };
        }
        setPayment_details(paymentDetails);
      }
    }, [selectedOrder, selectedPost, appeal]);

    const firstItem = 922;
    const secondItem = 518;
    const ratio = firstItem / (secondItem + firstItem);
    const totalColumns = 12;
    const firstItemColumns = Math.ceil(totalColumns * ratio);
    const secondItemColumns = totalColumns - firstItemColumns;

    const handleCloseAppeal = async () => {
      if (appeal_id) {
        setIsSubmiting(true);
        try {
          setConfirmDialog(null);
          commonStore.setActionState(
            UI_ACTION_STATE.WALLET_CONFIRMATION_PENDING,
          );
          await appealStore.close(appeal_id);
        } catch (error: any) {
          enqueueSnackbar(`Failed to close appeal: ${error.message}`, {
            variant: "error",
          });
        } finally {
          commonStore.setActionState(UI_ACTION_STATE.NONE);
        }
        setIsSubmiting(false);
      }
    };

    return (
      <Container
        maxWidth="xl"
        sx={{
          [theme.breakpoints.up("lg")]: { maxWidth: "xl" },
        }}
      >
        <Grid container justifyContent="stretch" alignContent="stretch">
          <Grid item sm={12} md={firstItemColumns}>
            <Box
              pl="75px"
              pr="20px"
              overflow="auto"
              sx={{
                height: {
                  sm: "fit-content",
                  md: `calc(100vh - ${theme.footerHeight} - ${theme.navbarHeight})`,
                },
              }}
            >
              <Box mt="48.7px" mb="26px">
                <Details
                  header="Appeal details"
                  dataList={appeal_details ?? {}}
                />
                <Details
                  header="Order details"
                  dataList={order_details ?? {}}
                  sx={{ mt: "48.5px" }}
                />
                <Details
                  header="Payment details"
                  dataList={payment_details ?? {}}
                  sx={{ mt: "43.5px" }}
                />
              </Box>
              <Card sx={{ py: "37px", mb: "73px" }}>
                <Grid
                  container
                  columnSpacing="69px"
                  rowSpacing="20px"
                  px="11.5063670411985%"
                >
                  <Grid
                    item
                    sm={appeal?.status === "CLOSED" ? 12 : 6}
                    textAlign="center"
                  >
                    <Button
                      variant={
                        appeal?.status === "CLOSED" ? "contained" : "outlined"
                      }
                      onClick={() => {
                        navigate(`/trade/orders/${appeal?.order_id}`);
                      }}
                      sx={{ width: "100%", maxWidth: "497.32px" }}
                    >
                      Back
                    </Button>
                  </Grid>
                  <Grid item sm={6}>
                    {appeal?.status !== "CLOSED" && (
                      <Button
                        variant="contained"
                        loading={isSubmiting}
                        onClick={() =>
                          setConfirmDialog({
                            title: "Close appeal",
                            createAppeal: true,
                          })
                        }
                        sx={{ width: "100%", maxWidth: "497.32px" }}
                      >
                        Close Appeal
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Card>
            </Box>
          </Grid>
          <Grid item sm={12} md={secondItemColumns}>
            {appeal_id && appeal && (
              <Chat
                other={{
                  id: otherId,
                  name: "Admin",
                }}
                chatMessages={chatAppealMessages}
                sendMessage={chatAppealStore.sendMessage}
                sendMessageId={appeal_id}
                closeData={{
                  isClosed: (appeal && appeal.status === "CLOSED") ?? false,
                  closeText: "The appeal is closed.",
                }}
              />
            )}
          </Grid>
        </Grid>
        {confirmDialog && (
          <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
            <DialogTitle variant="h3">Close appeal</DialogTitle>
            <DialogContent sx={{ pt: "20px !important" }}>
              <DialogContentText>
                Are you sure you want to close this appeal?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                loading={isSubmiting}
                onClick={() => setConfirmDialog(null)}
              >
                No
              </Button>
              <Button
                variant="contained"
                loading={isSubmiting}
                onClick={() => handleCloseAppeal()}
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Container>
    );
  }),
);
export default AppealPage;
